import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCode, FaChartLine, FaHeadset, FaTrophy, FaUsers, FaClock, FaCheckCircle } from 'react-icons/fa';
import SEO from '../components/SEO';
import { organizationSchema, websiteSchema } from '../schemas/structuredData';
import './Home.css';

const Home = () => {
  const services = [
    { icon: <FaCode />, title: 'Web Development', desc: 'Custom websites and web applications built with cutting-edge technology' },
    { icon: <FaChartLine />, title: 'Digital Marketing', desc: 'SEO, social media, and digital advertising strategies' },
    { icon: <FaHeadset />, title: 'IT Consulting', desc: 'Expert technology consulting and strategic planning' }
  ];

  const features = [
    { icon: <FaTrophy />, title: 'Industry Expertise', desc: 'Years of experience in I.T Field' },
    { icon: <FaUsers />, title: 'Dedicated Team', desc: 'Skilled professionals committed to your success' },
    { icon: <FaClock />, title: '24/7 Support', desc: 'Round-the-clock customer support and maintenance' },
    { icon: <FaCheckCircle />, title: 'Quality Guarantee', desc: 'Delivering excellence in every project' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="home">
      <SEO 
        title="HEGXIB Nigeria - Digital Solutions & Technology Services"
        description="Leading provider of digital solutions, technology services, web development, mobile apps, and IT consulting in Nigeria. Transform your business with cutting-edge technology."
        keywords="HEGXIB Nigeria, digital solutions, web development, mobile apps, IT consulting, software development, digital marketing, technology services Nigeria"
        url="https://ng.hegxib.me"
        structuredData={[organizationSchema, websiteSchema]}
      />
      {/* Hero Section */}
      <motion.section 
        className="hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-bg">
          <div className="hero-shape shape-1"></div>
          <div className="hero-shape shape-2"></div>
          <div className="hero-shape shape-3"></div>
        </div>
        
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            <motion.h1 
              className="hero-title"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              Transforming Nigeria's <span className="gradient-text">Digital Landscape</span>
            </motion.h1>
            
            <motion.p 
              className="hero-subtitle"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
              Leading technology and digital solutions provider empowering businesses across Nigeria
            </motion.p>
            
            <motion.div 
              className="hero-buttons"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
            >
              <Link to="/services" className="btn btn-primary">
                Our Services
              </Link>
              <Link to="/contact" className="btn btn-secondary">
                Get Started
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Services Preview */}
      <section className="services-preview">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">What We Do</h2>
            <p className="section-subtitle">Comprehensive digital solutions for modern businesses</p>
          </motion.div>

          <motion.div 
            className="services-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card"
                variants={itemVariants}
                whileHover={{ 
                  y: -10,
                  boxShadow: '0 20px 40px rgba(0, 135, 81, 0.2)'
                }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-us">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Why Choose HEGXIB Nigeria</h2>
          </motion.div>

          <motion.div 
            className="features-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <motion.section 
        className="cta"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="container">
          <motion.h2
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Ready to Transform Your Business?
          </motion.h2>
          <motion.p
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Let's discuss how we can help you achieve your digital goals
          </motion.p>
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            <Link to="/contact" className="btn btn-primary">
              Contact Us Today
            </Link>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default Home;
